import React, { useState } from 'react';
import Tooltip from '@cosmos/tooltip';
import Badge from '@cosmos/badge';

import Image from 'next/image';
import Link from 'next/link';
import CONFIG from 'config';

import { format } from 'date-fns';

import { Campaign } from '@/store/reducers/Campaign/campaignSlice';
import { Option } from '@/store/reducers/Campaign/campaignSlice';
import Place from '@/assets/icons/place.svg';
import InfoIcon from '@/assets/icons/icon-info.svg';
import { homePageTexts, routeTexts } from '@/assets/strings';
import { campaignImageSize } from '@/constants/imageSizes';
import { shimmer, toBase64 } from '@/utils/helpers/shimmer';
import Button from '@cosmos/button';

interface Props {
  campaign: Campaign;
  cities: Option[];
}

const CampaingCard: React.FC<Props> = ({ campaign, cities }) => {
  const getCityNamesByValue = (campaignCities: number[]) => {
    let cityNames: string[] = [];
    if (campaignCities.length >= 80) {
      return ['Tüm Türkiye'];
    }
    campaignCities.map((value: number) => {
      const cityObj = cities?.find((city: any) => city.value === value);
      cityNames = [...cityNames, cityObj?.label];
    });
    return cityNames;
  };
  const [cityNames] = useState<string[]>(getCityNamesByValue(campaign.cities));

  const dateDayDiff = (date: string) => {
    const today: any = new Date();
    const endDate: any = new Date(date);
    const diffTime = Math.abs(endDate - today);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatDate = (date: string) => {
    const currentDate = new Date(date);
    const stringDate = format(currentDate, 'dd.MM.yyyy');
    return stringDate;
  };

  const getImageNameFromBlob = (path: string) => {
    if (path.includes('blob')) {
      const pathParts = path.split('/');
      const result = pathParts[pathParts.length - 1];
      return `${CONFIG.ENDPOINTS.imageBasePath}${result}`;
    }
    return `${CONFIG.ENDPOINTS.imageBasePath}${path}`;
  };

  return (
    // eslint-disable-next-line @next/next/link-passhref
    <Link
      href={`${routeTexts.campaigns}/[slug]`}
      as={`${routeTexts.campaigns}/${campaign.slug}`}
    >
      <div
        className={
          'group relative shrink-0 flex-col max-w-[285px] border-2 rounded-[1.5rem] border-gray text-gray-500 text-center max-h-[330px] shadow-md shadow-gray600/10 hover:shadow-xl overflow-hidden ease-in duration-300	cursor-pointer'
        }
      >
        <Image
          src={getImageNameFromBlob(campaign.images[0].uploadedImagePath)}
          alt='banner'
          width={campaignImageSize.width}
          height={campaignImageSize.height}
          layout='responsive'
          className={
            'rounded-t-[1.4rem] group-hover:scale-110 ease-in duration-300'
          }
          placeholder='blur'
          blurDataURL={`data:image/svg+xml;base64,${toBase64(
            shimmer(700, 475)
          )}`}
        />

        <div className='flex flex-col items-center my-3'>
          <div className='drop-shadow-md'>
            <Image
              className='w-[60px] h-[60px] cursor-pointer object-cover rounded-md'
              src={`${CONFIG.ENDPOINTS.imageBasePath}${campaign.partnerLogo}`}
              layout='fixed'
              objectFit='cover'
              width={60}
              height={60}
              alt=''
            />
          </div>
          <p className={'text-[14px] font-[400] text-black '}>
            {campaign.partnerName}
          </p>
          <p
            className={
              'text-[16px] font-[600] text-center text-[#212121] min-w-[250px]  line-clamp-1	break-words mx-2   min-h-[1rem] mt-[4px]'
            }
          >
            {campaign.name}
          </p>
          <div className='min-h-[1.5rem]'>
            {campaign.endDate ? (
              <p className={'font-[400] text-[12px] mt-[4px] text-[#666666]'}>
                {homePageTexts.lastDay}: {formatDate(campaign.endDate)}
              </p>
            ) : null}
          </div>
          <div
            className={'flex justify-center items-center gap-[0.2rem] my-[8px]'}
          >
            <Image
              src={Place}
              alt='Place'
              layout='fixed'
              width={14}
              height={12}
              className={'drop-shadow-md'}
            />

            <span
              data-testid='test-span'
              className={
                'text-[12px] text-ellipsis overflow-hidden max-w-[10rem] text-[#666666] '
              }
            >
              {cityNames?.toString()}
            </span>
            {cityNames?.length > 0 && cityNames[0] !== 'Tüm Türkiye' && (
              <Tooltip content={cityNames?.join(', ')} placement='bottom'>
                {cityNames?.length > 3 && (
                  <div className='max-h-[12px] flex'>
                    <Image
                      src={InfoIcon}
                      layout='fixed'
                      width={12}
                      height={12}
                      alt='icon-info'
                    />
                  </div>
                )}
              </Tooltip>
            )}
          </div>
          <div className={'text-[14px] mt-2  flex'}>
            <Link
              href={`${routeTexts.campaigns}/[slug]`}
              as={`${routeTexts.campaigns}/${campaign.slug}`}
            >
              <Button kind='ghost' className='card-button relative '>
                <div className={'text-[14px]  my-4 '}>
                  {dateDayDiff(campaign.endDate) < 999 ? (
                    <div className='flex flex-row justify-between  '>
                      <Badge
                        color={'#FF6000'}
                        className=' absolute bottom-[74%] left-[5%] border-2 border-white !py-0'
                      >
                        <p className={'text-[12px]  '}>
                          Son {dateDayDiff(campaign.endDate)} Gün
                        </p>
                      </Badge>
                    </div>
                  ) : null}
                </div>
                <a
                  className={'font-bold hover:text-orange hover:cursor-pointer'}
                >
                  {homePageTexts.detailedInfo}
                </a>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CampaingCard;
